'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useUpdateLocation } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Location01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
// keeps raw Input because: dialog-internal search filter; no label/description/error
import { InputField } from '@/components/ui/input-field';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { COUNTRIES, countryCodeToFlag, getCountryByCode, normalizeCountryCode } from '@/config/countries';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
export interface LocationSelectorProps {
  /** When set, dialog is controlled by parent; no trigger button is rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Headless selection state for the location picker. Owned by the wrapper that
 * renders the SilkSheet footer (standalone `LocationSelector` or the combined
 * `LocaleLocationModal`).
 */
export interface LocationSelectionState {
  currentCountry: string | null;
  selected: string | null;
  setSelected: (code: string | null) => void;
  search: string;
  setSearch: (s: string) => void;
  hasChange: boolean;
  loading: boolean;
  apply: (onClose: () => void) => Promise<void>;
}
export function useLocationSelection() {
  const $ = _c(15);
  const t = useTranslations("location");
  const {
    user,
    setUser
  } = useAuth();
  const {
    mutateAsync: updateLocation
  } = useUpdateLocation();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const t0 = user?.country;
  let t1;
  if ($[0] !== t0) {
    t1 = normalizeCountryCode(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentCountry = t1;
  let t2;
  if ($[2] !== selected || $[3] !== setUser || $[4] !== t || $[5] !== updateLocation || $[6] !== user) {
    t2 = async function apply(onClose) {
      if (selected === null || user === null) {
        return;
      }
      setLoading(true);
      await runWithCleanup(async () => {
        ;
        try {
          await updateLocation({
            country: selected
          });
          setUser({
            ...user,
            country: selected,
            locationSource: "manual"
          });
          onEvent("location_changed", {
            country: selected,
            source: "manual"
          });
          showToast({
            id: TOAST_IDS.LOCATION_UPDATED,
            message: t("success"),
            variant: "success"
          });
          setSearch("");
          setSelected(null);
          onClose();
        } catch (t3) {
          const err = t3;
          logger.error("Failed to update location", {
            error: err instanceof Error ? err.message.slice(0, 200) : String(err)
          });
          showToast({
            id: TOAST_IDS.PROFILE_ERROR,
            message: t("success"),
            variant: "error"
          });
        }
      }, () => setLoading(false));
    };
    $[2] = selected;
    $[3] = setUser;
    $[4] = t;
    $[5] = updateLocation;
    $[6] = user;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const apply = t2;
  const t3 = selected !== null && selected !== currentCountry;
  let t4;
  if ($[8] !== apply || $[9] !== currentCountry || $[10] !== loading || $[11] !== search || $[12] !== selected || $[13] !== t3) {
    t4 = {
      currentCountry,
      selected,
      setSelected,
      search,
      setSearch,
      hasChange: t3,
      loading,
      apply
    };
    $[8] = apply;
    $[9] = currentCountry;
    $[10] = loading;
    $[11] = search;
    $[12] = selected;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export interface LocationSelectorContentProps {
  state: LocationSelectionState;
}
export interface LocationPickerProps {
  currentCountry: string | null;
  selected: string | null;
  onSelectedChange: (code: string | null) => void;
}

/** Searchable country list for forms that persist location on submit. */
export function LocationPicker(t0) {
  const $ = _c(5);
  const {
    currentCountry,
    selected,
    onSelectedChange
  } = t0;
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] !== currentCountry || $[1] !== onSelectedChange || $[2] !== search || $[3] !== selected) {
    t1 = <LocationSelectorContent state={{
      currentCountry,
      selected,
      setSelected: onSelectedChange,
      search,
      setSearch,
      hasChange: false,
      loading: false,
      apply: _temp
    }} />;
    $[0] = currentCountry;
    $[1] = onSelectedChange;
    $[2] = search;
    $[3] = selected;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * Controlled body for location selection (no Dialog, no action button).
 *
 * Combobox, not a permanently-open list: picking a country collapses the list
 * and the field reads back the selection (flag + name). Focusing the field
 * reopens it and clears the query so the full list is browsable again. It
 * starts collapsed whenever a country is already known — the IP-detected one
 * during onboarding, the saved one in settings — so the common case is
 * "here's where we think you are", not a wall of 200 countries.
 */
async function _temp() {}
export function LocationSelectorContent(t0) {
  const $ = _c(27);
  const {
    state
  } = t0;
  const t = useTranslations("location");
  const {
    selected,
    setSelected,
    search,
    setSearch,
    currentCountry
  } = state;
  const displayCountry = normalizeCountryCode(selected ?? currentCountry);
  const [isOpen, setIsOpen] = useState(displayCountry === null);
  const collapsedValue = displayCountry === null ? "" : `${countryCodeToFlag(displayCountry)} ${getCountryByCode(displayCountry)?.name ?? displayCountry}`;
  let t1;
  if ($[0] !== collapsedValue || $[1] !== displayCountry || $[2] !== isOpen || $[3] !== search || $[4] !== setIsOpen || $[5] !== setSearch || $[6] !== setSelected || $[7] !== t) {
    const filtered = search.length === 0 ? COUNTRIES : COUNTRIES.filter(c => {
      const q = search.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
    });
    let t2;
    if ($[9] !== isOpen || $[10] !== setIsOpen || $[11] !== setSearch) {
      t2 = function openList() {
        if (isOpen) {
          return;
        }
        setSearch("");
        setIsOpen(true);
      };
      $[9] = isOpen;
      $[10] = setIsOpen;
      $[11] = setSearch;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    const openList = t2;
    let t3;
    if ($[13] !== setIsOpen || $[14] !== setSearch || $[15] !== setSelected) {
      t3 = function pick(code) {
        setSelected(code);
        setSearch("");
        setIsOpen(false);
      };
      $[13] = setIsOpen;
      $[14] = setSearch;
      $[15] = setSelected;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    const pick = t3;
    let t4;
    if ($[17] !== t) {
      t4 = t("search_placeholder");
      $[17] = t;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    const t5 = isOpen ? search : collapsedValue;
    let t6;
    if ($[19] !== openList || $[20] !== setSearch) {
      t6 = e => {
        openList();
        setSearch(e.target.value);
      };
      $[19] = openList;
      $[20] = setSearch;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    let t7;
    if ($[22] !== openList || $[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
      t7 = <InputField placeholder={t4} value={t5} onChange={t6} onFocus={openList} containerClassName="my-2" />;
      $[22] = openList;
      $[23] = t4;
      $[24] = t5;
      $[25] = t6;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    t1 = <>{t7}{isOpen ? <div className="h-64 overflow-y-auto rounded-control border bg-background-primary">{filtered.length === 0 && <p className="flex h-full items-center justify-center px-4 text-center text-body text-foreground-secondary">{t("no_results")}</p>}{filtered.map(country => {
          const isSelectedCountry = displayCountry === country.code;
          return <button key={country.code} type="button" className="flex w-full items-center gap-3 px-3 py-2 text-left text-body transition-colors hover:bg-background-tertiary" onClick={() => {
            pick(country.code);
          }}><span className="text-heading-md">{countryCodeToFlag(country.code)}</span><span className="flex-1">{country.name}</span>{isSelectedCountry && <Icon icon={Tick02Icon} className="size-4 text-foreground-primary" aria-hidden="true" />}</button>;
        })}</div> : null}</>;
    $[0] = collapsedValue;
    $[1] = displayCountry;
    $[2] = isOpen;
    $[3] = search;
    $[4] = setIsOpen;
    $[5] = setSearch;
    $[6] = setSelected;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
export function LocationSelector(t0) {
  const $ = _c(40);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange
  } = t1;
  const t = useTranslations("location");
  const {
    user,
    isAuthenticated
  } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const state = useLocationSelection();
  const isControlled = controlledOnOpenChange !== undefined;
  const open = isControlled ? controlledOpen ?? false : internalOpen;
  let t2;
  if ($[2] !== controlledOnOpenChange || $[3] !== isControlled) {
    t2 = isControlled ? controlledOnOpenChange ?? _temp2 : setInternalOpen;
    $[2] = controlledOnOpenChange;
    $[3] = isControlled;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const setOpen = t2;
  const [previousOpen, setPreviousOpen] = useState(open);
  if (open !== previousOpen) {
    setPreviousOpen(open);
    if (open) {
      state.setSelected(null);
    }
  }
  if (!isAuthenticated || user === null) {
    return null;
  }
  let t3;
  if ($[5] !== t || $[6] !== user.country) {
    t3 = user.country ? `${countryCodeToFlag(user.country)} ${getCountryByCode(user.country)?.name ?? user.country}` : t("not_set");
    $[5] = t;
    $[6] = user.country;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const triggerLabel = t3;
  let t4;
  if ($[8] !== setOpen) {
    t4 = function handleOpenChange(isOpen) {
      setOpen(isOpen);
    };
    $[8] = setOpen;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[10] !== t) {
    t5 = t("title");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("disclaimer");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isControlled || $[15] !== t || $[16] !== triggerLabel || $[17] !== user.country) {
    t7 = !isControlled ? <AnimatedButton variant="tertiary" aria-label={t("label")} title={triggerLabel} className="size-9 p-0">{user.country ? <span className="text-heading-md">{countryCodeToFlag(user.country)}</span> : <Icon icon={Location01Icon} className="size-4" aria-hidden="true" />}</AnimatedButton> : undefined;
    $[14] = isControlled;
    $[15] = t;
    $[16] = triggerLabel;
    $[17] = user.country;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== setOpen || $[20] !== state) {
    t8 = () => {
      state.apply(() => setOpen(false));
    };
    $[19] = setOpen;
    $[20] = state;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = !state.hasChange || state.loading;
  let t10;
  if ($[22] !== state.loading || $[23] !== t) {
    t10 = state.loading ? t("updating") : t("update_button");
    $[22] = state.loading;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== state.loading || $[26] !== t10 || $[27] !== t8 || $[28] !== t9) {
    t11 = <ModalFooter><AnimatedButton onClick={t8} disabled={t9} loading={state.loading}>{t10}</AnimatedButton></ModalFooter>;
    $[25] = state.loading;
    $[26] = t10;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== state) {
    t12 = <LocationSelectorContent state={state} />;
    $[30] = state;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== handleOpenChange || $[33] !== open || $[34] !== t11 || $[35] !== t12 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7) {
    t13 = <SilkSheet open={open} onOpenChange={handleOpenChange} variant="detached" size="md" keyboard={true} title={t5} description={t6} trigger={t7} footer={t11}>{t12}</SilkSheet>;
    $[32] = handleOpenChange;
    $[33] = open;
    $[34] = t11;
    $[35] = t12;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
}
function _temp2(_v) {}